import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Box,
  Typography,
  TextField,
  Collapse,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
  useTheme,
  alpha,
  Menu, 
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  Edit,
  Delete as DeleteIcon,
  Print,
  ExpandMore,
  ExpandLess,
  EmojiEvents,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  QuotationListApi,
  QuotationDelete,
  updateQuotationStatus,
  updateQuotationResult,      // ✅ NEW
  QuotationListByResultApi,   // ✅ NEW
} from "../../../Services/Services";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { toast, ToastContainer } from "react-toastify";
import { generatePdf } from "./QuotationPrint";
pdfMake.vfs = pdfFonts.vfs;

export default function QuotationTable({ resultFilter = null }) {
  // ✅ NEW — Page type detection
  const isMainPage = !resultFilter;
  const isWinPage = resultFilter === "win";
  const isLosePage = resultFilter === "lose";
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ── Theme-aware helpers ──
  // ============================================================
  // ✅✅✅ FIX #1: tableHeadBg — SOLID hex (alpha NAHI)
  // ❌ pehle: alpha(theme.palette.primary.main, 0.15) — translucent
  // Sticky header cells (S.No. / Ref No / Action) isi ko use karte
  // hain, isliye dark mode me peeche ka content dikh raha tha.
  // ============================================================
  const tableHeadBg = isDark ? "#252b38" : "#f5f5f5";

  // ============================================================
  // ✅✅✅ FIX #2: rowBg — SOLID zebra (alpha NAHI)
  // ❌ pehle dark me: alpha(theme.palette.common.white, 0.035)
  // Sticky body cells "inherit" se yehi color lete hain,
  // isliye opaque hona zaroori hai.
  // ============================================================
  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e" // even row - dark (solid)
        : "#1a1d26" // odd row - dark (solid)
      : index % 2 === 0
      ? "#fafafa" // even row - light (same as before)
      : "#ffffff"; // odd row - light (same as before)

  // ============================================================
  // ✅✅✅ FIX #3: rowHoverBg — SOLID hover color
  // ❌ pehle dark me: alpha(t.palette.primary.main, 0.1)
  // ============================================================
  const rowHoverBg = isDark ? "#2c3242" : "#e3f2fd";

  // ============================================================
  // ✅✅✅ FIX #4: S.No column width LOCK = 76px
  // ❌ Problem: pehle S.No header ka actual width ~76px tha
  //    (text + 32px padding), lekin Ref No ko "left: 60" diya
  //    tha — isliye Ref No, S.No ke UPAR 16px overlap karta tha!
  // ✅ Ab S.No ka width exact 76 lock kiya (content Box se),
  //    aur Ref No ka left = 76. Kabhi overlap nahi hoga.
  // ============================================================
  const SNO_COL_WIDTH = 76;
  const REFNO_COL_LEFT = SNO_COL_WIDTH; // 76 (S.No ke exactly baad)

  const [open, setOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [expandedQuotation, setExpandedQuotation] = useState(null);
    // ✅ NEW — Result action states
  const [resultMenuAnchor, setResultMenuAnchor] = useState(null);
  const [resultQuoteId, setResultQuoteId] = useState(null);
  const [winDialogOpen, setWinDialogOpen] = useState(false);
  const [loseDialogOpen, setLoseDialogOpen] = useState(false);
  const [loseReason, setLoseReason] = useState("");

  const [filters, setFilters] = useState({
    to_company: "",
    to_name: "",
    ref_no: "",
    created_at: "",
  });

  const handleClick = () => {
    navigate("/dashboard/quotationForm");
  };

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        let response;

        if (resultFilter) {
          // Win/Lose page — filter by result
          response = await QuotationListByResultApi(resultFilter);
        } else {
          // Main page — fetch ALL quotations (no filter)
          try {
            response = await QuotationListApi();
          } catch (err) {
            // Fallback: agar QuotationListApi kaam na kare
            console.warn("QuotationListApi failed, trying without filter:", err);
            response = await QuotationListByResultApi("pending");
          }
        }

        // Handle different response formats
        const data = Array.isArray(response)
          ? response
          : response?.results
            ? response.results
            : response?.data
              ? response.data
              : [];

        console.log("📋 Quotations loaded:", data.length, "items");
        setQuotations(data);
      } catch (error) {
        console.error("Error fetching quotations:", error);
        toast.error("Failed to load quotations");
        setQuotations([]);
      }
    };
    fetchQuotations();
  }, [resultFilter]);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const handleView = (quote) => {
    setSelectedQuote(quote);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const deleted = await QuotationDelete(id);
      if (Array.isArray(deleted)) {
        toast.success("Quotation deleted successfully!");
        setQuotations(deleted);
      } else {
        toast.error("Failed to delete quotation!");
      }
    } catch (error) {
      console.error("Error deleting quotation:", error);
      toast.error("Error deleting quotation!");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQuote(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
    // ✅ NEW — Result action handlers
  const handleResultMenuOpen = (event, quoteId) => {
    setResultMenuAnchor(event.currentTarget);
    setResultQuoteId(quoteId);
  };

  const handleWinConfirm = async () => {
    try {
      await updateQuotationResult(resultQuoteId, "win");
      setQuotations((prev) =>
        prev.filter((q) => q.quotation_id !== resultQuoteId)
      );
      toast.success("Quotation marked as WON! 🎉");
    } catch (err) {
      toast.error("Failed to update result");
    }
    setWinDialogOpen(false);
    setResultQuoteId(null);
  };

  const handleLoseConfirm = async () => {
    if (!loseReason.trim()) {
      toast.warning("Please enter a reason for losing!");
      return;
    }
    try {
      await updateQuotationResult(resultQuoteId, "lose", loseReason);
      setQuotations((prev) =>
        prev.filter((q) => q.quotation_id !== resultQuoteId)
      );
      toast.success("Quotation marked as LOST");
    } catch (err) {
      toast.error("Failed to update result");
    }
    setLoseDialogOpen(false);
    setLoseReason("");
    setResultQuoteId(null);
  };

  const STATUS_OPTIONS = [
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const statusColor = (s) => {
    if (s === "approved")
      return {
        bg: isDark ? alpha("#4caf50", 0.18) : "#e8f5e9",
        color: "#2e7d32",
      };
    if (s === "cancelled")
      return {
        bg: isDark ? alpha("#f44336", 0.18) : "#ffebee",
        color: "#d32f2f",
      };
    if (s === "pending")
      return {
        bg: isDark ? alpha("#ff9800", 0.18) : "#fff3e0",
        color: "#ed6c02",
      };
    return {
      bg: isDark ? alpha(theme.palette.common.white, 0.06) : "#f5f5f5",
      color: isDark ? "#aaa" : "#666",
    };
  };

  const handleStatusChange = async (quote, newStatus) => {
    const prev = quote.status;
    setQuotations((list) =>
      list.map((q) =>
        q.quotation_id === quote.quotation_id
          ? { ...q, status: newStatus }
          : q
      )
    );
    try {
      await updateQuotationStatus(quote.quotation_id, newStatus);
      toast.success("Status updated");
    } catch (err) {
      setQuotations((list) =>
        list.map((q) =>
          q.quotation_id === quote.quotation_id ? { ...q, status: prev } : q
        )
      );
      toast.error("Status save nahi hua");
    }
  };

  const filteredQuotations = quotations.filter((quote) => {
    return (
      quote.to_company
        .toLowerCase()
        .includes(filters.to_company.toLowerCase()) &&
      quote.to_name
        .toLowerCase()
        .includes(filters.to_name.toLowerCase()) &&
      quote.ref_no.toLowerCase().includes(filters.ref_no.toLowerCase())
    );
  });

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: -3,
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── FILTERS BAR ── */}
      <Box
        sx={{
          mb: 2,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Company"
              name="to_company"
              value={filters.to_company}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Party Name"
              name="to_name"
              value={filters.to_name}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Ref No"
              name="ref_no"
              value={filters.ref_no}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Date"
              name="created_at"
              type="date"
              value={filters.created_at}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>
           {isMainPage && (
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleClick}
                startIcon={<AddIcon />}
                sx={{ height: 32, fontSize: "0.7rem", borderRadius: 0 }}
              >
                Quotation
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* ── MOBILE VIEW (CARDS) ── */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredQuotations.map((quote, index) => (
            <Box key={quote.quotation_id}>
              {/* Main Row */}
              <Box
                onClick={() =>
                  setExpandedQuotation(
                    expandedQuotation === quote.quotation_id
                      ? null
                      : quote.quotation_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: rowBg(index),
                  "&:active": { bgcolor: "action.selected" },
                  borderLeft: "4px solid",
                  borderLeftColor: "primary.main",
                  transition: "background-color 0.15s ease",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                        color: "text.primary",
                      }}
                    >
                      {quote.ref_no}
                    </Typography>
                    <Chip
                      label={quote.date}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: "primary.main",
                        "& .MuiChip-label": { px: 1, color: "#ffffff" },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {quote.to_title ? `${quote.to_title} ` : ""}
                      {quote.to_name.substring(0, 20)}
                    </span>
                    <span>•</span>
                    <span style={{ fontSize: "0.7rem" }}>
                      {quote.created_at
                        ? new Date(quote.created_at)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")
                        : "--"}
                    </span>
                  </Typography>
                </Box>

                <IconButton size="small">
                  {expandedQuotation === quote.quotation_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              {/* Expanded Details */}
              <Collapse
                in={expandedQuotation === quote.quotation_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.common.white, 0.05)
                        : alpha(t.palette.primary.main, 0.04),
                    borderLeft: "4px solid",
                    borderLeftColor: "primary.main",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    {/* Status */}
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        STATUS
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          displayEmpty
                          value={quote.status || "pending"}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            handleStatusChange(quote, e.target.value)
                          }
                          sx={{
                            height: 28,
                            fontSize: "0.72rem",
                            bgcolor: statusColor(quote.status).bg,
                            color: statusColor(quote.status).color,
                            fontWeight: 600,
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select status
                          </MenuItem>
                          {STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Company */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        COMPANY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {quote.to_company}
                      </Typography>
                    </Box>

                    {/* Contact */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        CONTACT
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {quote.contact_no}
                      </Typography>
                    </Box>

                    {/* Email */}
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        EMAIL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "text.secondary",
                          wordBreak: "break-all",
                        }}
                      >
                        {quote.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1, borderColor: "divider" }} />

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.6,
                    }}
                  >
                    {/* Edit */}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate("/dashboard/quotationForm", {
                          state: { data: quote },
                        });
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.28)
                              : "#bbdefb",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: "1rem", color: "primary.main" }} />
                    </IconButton>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAnchorEl(event.currentTarget);
                        setQuoteToDelete(quote.quotation_id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem", color: "error.main" }} />
                    </IconButton>

                    {/* Print */}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        generatePdf(quote);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha("#7b1fa2", 0.15)
                            : "#f3e5f5",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha("#7b1fa2", 0.28)
                              : "#e1bee7",
                        },
                      }}
                    >
                      <Print sx={{ fontSize: "1rem", color: "#7b1fa2" }} />
                    </IconButton>
                    {/* ✅ NEW — Result action mobile (Main page only) */}
                    {isMainPage && (
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleResultMenuOpen(event, quote.quotation_id);
                        }}
                        sx={{
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? "rgba(255,152,0,0.15)"
                              : "#fff3e0",
                          borderRadius: "6px",
                          width: 32,
                          height: 32,
                          "&:hover": {
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? "rgba(255,152,0,0.28)"
                                : "#ffe0b2",
                          },
                        }}
                      >
                        <EmojiEvents sx={{ fontSize: "1rem", color: "#ff9800" }} />
                      </IconButton>
                    )}

                     
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* ── DESKTOP TABLE VIEW ── */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: "88vh",
            overflow: "auto",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1400,
              "& .MuiTableCell-root": {
                borderBottom: "1px solid",
                borderColor: "divider",
                whiteSpace: "nowrap",
                padding: "8px 16px !important",
              },
            }}
          >
            <TableHead
              sx={{
                "& th": {
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  letterSpacing: "0.04em",
                },
              }}
            >
              <TableRow>
                {/* ✅✅✅ FIX #4 (header): S.No — width 76 LOCK */}
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    width: SNO_COL_WIDTH,
                    minWidth: SNO_COL_WIDTH,
                    backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                    zIndex: 10,
                  }}
                  align="center"
                >
                  {/* Content width lock — taaki column kabhi stretch na ho */}
                  <Box
                    sx={{
                      width: 44,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mx: "auto",
                    }}
                  >
                    S.No.
                  </Box>
                </TableCell>

                {/* ✅✅✅ FIX #4 (header): Ref No — left: 76 (pehle 60 tha,
                    isliye S.No ke upar overlap hota tha) */}
                <TableCell
                  sx={{
                    position: "sticky",
                    left: REFNO_COL_LEFT,
                    backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                    zIndex: 10,
                    minWidth: 130,
                  }}
                  align="center"
                >
                  Ref No
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 100 }}>
                  Date
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  To Name
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 200 }}>
                  Company
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  Contact No.
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 200 }}>
                  Email
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  Status
                </TableCell>
                {isLosePage && (
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    Lose Reason
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                    zIndex: 10,
                    minWidth: 120,
                    boxShadow: isDark
                      ? "-2px 0 5px -2px rgba(0,0,0,0.4)"
                      : "-2px 0 5px -2px rgba(0,0,0,0.2)",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredQuotations.map((quote, qIdx) => (
                /* ✅✅✅ FIX #5: "hover" prop HATA DIYA + SOLID zebra + SOLID hover */
                <TableRow
                  key={quote.quotation_id}
                  sx={{
                    backgroundColor: rowBg(qIdx),
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: `${rowHoverBg} !important`, // ✅ SOLID
                      "& .MuiTableCell-root": {
                        backgroundColor: "inherit !important",
                      },
                    },
                    "& td": { color: "text.primary" },
                  }}
                >
                  {/* ✅✅✅ FIX #4 (body): S.No — width 76 LOCK */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      width: SNO_COL_WIDTH,
                      minWidth: SNO_COL_WIDTH,
                      bgcolor: "inherit", // ✅ row SOLID hai — inherit theek hai
                      zIndex: 5,
                    }}
                    align="center"
                  >
                    {qIdx + 1}
                  </TableCell>

                  {/* ✅✅✅ FIX #4 (body): Ref No — left: 76 */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: REFNO_COL_LEFT,
                      bgcolor: "inherit", // ✅ theek hai ab
                      zIndex: 5,
                      minWidth: 130,
                    }}
                    align="center"
                  >
                    {quote.ref_no}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 100 }}>
                    {quote.date}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 150 }}>
                    {quote.to_title ? `${quote.to_title} ` : ""}
                    {quote.to_name}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {quote.to_company}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    {quote.contact_no}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {quote.email}
                  </TableCell>

                  {/* Status Select */}
                  <TableCell align="center" sx={{ minWidth: 150 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        displayEmpty
                        value={quote.status || ""}
                        onChange={(e) =>
                          handleStatusChange(quote, e.target.value)
                        }
                        sx={{
                          height: 28,
                          fontSize: "0.75rem",
                          bgcolor: statusColor(quote.status).bg,
                          color: statusColor(quote.status).color,
                          fontWeight: 600,
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select status
                        </MenuItem>
                        {STATUS_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                   {isLosePage && (
                    <TableCell align="center" sx={{ minWidth: 200 }}>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "error.main",
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={quote.lose_reason}
                      >
                        {quote.lose_reason || "—"}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Actions — sticky right, "inherit" theek hai ✅ */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      bgcolor: "inherit",
                      zIndex: 5,
                      minWidth: 120,
                      boxShadow: isDark
                        ? "-2px 0 5px -2px rgba(0,0,0,0.4)"
                        : "-2px 0 5px -2px rgba(0,0,0,0.1)",
                    }}
                    align="center"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          navigate("/dashboard/quotationForm", {
                            state: { data: quote },
                          })
                        }
                        sx={{ p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setQuoteToDelete(quote.quotation_id);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="default"
                        onClick={() => generatePdf(quote)}
                        sx={{ p: 0.5 }}
                      >
                        <Print sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      {isMainPage && (
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleResultMenuOpen(e, quote.quotation_id)
                          }
                          sx={{
                            p: 0.5,
                            color: "#ff9800",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(255,152,0,0.15)"
                                  : "#fff3e0",
                            },
                          }}
                        >
                          <EmojiEvents sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── DELETE POPOVER ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQuoteToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setQuoteToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                await handleDelete(quoteToDelete);
                setAnchorEl(null);
                setQuoteToDelete(null);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── VIEW DIALOG ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            pb: 0,
            fontSize: "1.25rem",
            lineHeight: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.12)
                : alpha(t.palette.primary.main, 0.05),
          }}
        >
          Quotation Details - {selectedQuote?.ref_no}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 3,
            fontSize: "0.95rem",
            lineHeight: 1.7,
            bgcolor: "background.paper",
          }}
        >
          {selectedQuote && (
            <Stack spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ color: "text.primary" }}>
                  <strong>To:</strong>{" "}
                  {selectedQuote.to_title ? `${selectedQuote.to_title} ` : ""}
                  {selectedQuote.to_name} ({selectedQuote.to_company})
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  <strong>Address:</strong> {selectedQuote.address}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  <strong>Date:</strong> {selectedQuote.date}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  <strong>Subject:</strong> {selectedQuote.subject}
                </Typography>
                {selectedQuote.body && (
                  <Typography sx={{ color: "text.secondary" }}>
                    <p>To {selectedQuote.to_company},</p>
                    {selectedQuote.body}
                  </Typography>
                )}
              </Grid>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  backgroundImage: "none",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Table size="small">
                  <TableHead
                    sx={{
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.12)
                          : "#f9f9f9",
                      "& th": { color: "text.primary", fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      <TableCell align="center">Sl. No.</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate (₹)</TableCell>
                      <TableCell align="right">Total (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedQuote.items_list.map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          "& td": {
                            color: "text.primary",
                            borderColor: "divider",
                          },
                        }}
                      >
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{item.rate}</TableCell>
                        <TableCell align="right">{item.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                align="right"
                sx={{ fontWeight: "bold", mt: 1, color: "text.primary" }}
              >
                Total Amount: ₹ {selectedQuote.total_amount}
              </Typography>

              <Grid item xs={12} sm={6}>
                {[
                  { label: "GST %", value: selectedQuote.gst_percentage },
                  { label: "Validity", value: selectedQuote.validity },
                  { label: "PF", value: selectedQuote.pf },
                  {
                    label: "Transport/Insurance",
                    value: selectedQuote.transport_insurance,
                  },
                  { label: "Delivery", value: selectedQuote.delivery },
                  { label: "Warranty", value: selectedQuote.warranty },
                  {
                    label: "Inspection Required",
                    value: selectedQuote.inspection_required ? "Yes" : "No",
                  },
                ].map(({ label, value }) => (
                  <Typography key={label} sx={{ color: "text.secondary" }}>
                    <strong>{label}:</strong> {value}
                  </Typography>
                ))}

                {selectedQuote.payment_terms?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Payment Terms:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.payment_terms.map((term, idx) => (
                        <li key={idx} style={{ color: theme.palette.text.secondary }}>
                          {term}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                {selectedQuote.scope_of_work?.length > 0 && (
                  <>
                    <Typography
                      sx={{ fontWeight: "bold", mb: 0, color: "text.primary" }}
                    >
                      Scope of Work:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.scope_of_work.map((item, idx) => (
                        <li key={idx} style={{ color: theme.palette.text.secondary }}>
                          {item}
                        </li>
                      ))}
                    </Box>
                  </>
                )}

                {selectedQuote.exclusions?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Exclusions:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.exclusions.map((item, idx) => (
                        <li key={idx} style={{ color: theme.palette.text.secondary }}>
                          {item}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
            {/* ── PAGE TITLE (Win/Lose) ── */}
      {!isMainPage && (
        <Box
          sx={{
            mb: 1,
            px: 1,
            py: 0.5,
            bgcolor: isWinPage
              ? isDark ? "rgba(76,175,80,0.15)" : "#e8f5e9"
              : isDark ? "rgba(244,67,54,0.15)" : "#ffebee",
            borderLeft: "4px solid",
            borderLeftColor: isWinPage ? "success.main" : "error.main",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1rem",
              color: isWinPage ? "success.main" : "error.main",
            }}
          >
            {isWinPage ? "🏆 Won Quotations" : "❌ Lost Quotations"}
          </Typography>
        </Box>
      )}

      {/* ── RESULT MENU ── */}
      <Menu
        anchorEl={resultMenuAnchor}
        open={Boolean(resultMenuAnchor)}
        onClose={() => setResultMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
            minWidth: 140,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setResultMenuAnchor(null);
            setWinDialogOpen(true);
          }}
          sx={{
            fontSize: "0.85rem",
            gap: 1,
            "&:hover": { bgcolor: isDark ? "rgba(76,175,80,0.15)" : "#e8f5e9" },
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>🏆</span> Win
        </MenuItem>
        <MenuItem
          onClick={() => {
            setResultMenuAnchor(null);
            setLoseDialogOpen(true);
          }}
          sx={{
            fontSize: "0.85rem",
            gap: 1,
            "&:hover": { bgcolor: isDark ? "rgba(244,67,54,0.15)" : "#ffebee" },
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>❌</span> Lose
        </MenuItem>
      </Menu>

      {/* ── WIN CONFIRMATION DIALOG ── */}
      <Dialog
        open={winDialogOpen}
        onClose={() => setWinDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            minWidth: 340,
          },
        }}
      >
        <DialogTitle sx={{ color: "success.main", fontWeight: "bold" }}>
          🏆 Mark as Won?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.primary", mt: 1 }}>
            Are you sure you want to mark this quotation as{" "}
            <strong style={{ color: "#4caf50" }}>WON</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWinDialogOpen(false)} variant="outlined">
            No
          </Button>
          <Button
            onClick={handleWinConfirm}
            variant="contained"
            color="success"
          >
            Yes, Won!
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── LOSE DIALOG WITH REASON ── */}
      <Dialog
        open={loseDialogOpen}
        onClose={() => {
          setLoseDialogOpen(false);
          setLoseReason("");
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>
          ❌ Mark as Lost?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.primary", mt: 1, mb: 2 }}>
            Please provide a reason for losing this quotation:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="e.g. Client chose competitor, pricing too high, etc."
            value={loseReason}
            onChange={(e) => setLoseReason(e.target.value)}
            required
            sx={{
              "& .MuiInputBase-root": {
                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#fafafa",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setLoseDialogOpen(false);
              setLoseReason("");
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleLoseConfirm}
            variant="contained"
            color="error"
            disabled={!loseReason.trim()}
          >
            Yes, Lost
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
